/**
 * AlphaEnginePage — Alpha Engine Performance Dashboard
 *
 * Shows auto-pot performance, recent trades, signal attribution,
 * and multi-source convergences driving the autonomous trading engine.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Activity, TrendingUp, TrendingDown, Trophy, Target,
  BarChart2, RefreshCw, Zap, Clock, AlertTriangle,
  CheckCircle, XCircle, Pause, Play, Info, Gauge,
  ShieldCheck, Layers, HelpCircle,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from 'recharts'
import { api } from '../lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface Trade {
  id?: number
  ticker: string
  action: 'BUY' | 'SELL'
  qty: number
  entry_price?: number
  current_price?: number
  exit_price?: number
  pnl?: number
  pnl_pct?: number
  conviction?: number | string
  status?: string
  signal_sources?: string[]
  created_at?: string
  executed_at?: string
  closed_at?: string
  duration?: string
}

interface Signal {
  source: string
  signal_type: string
  ticker: string
  company: string
  sentiment: number
  confidence: number
  summary: string
  timestamp: string
  score: number
}

interface Convergence {
  ticker: string
  direction: 'bullish' | 'bearish' | 'mixed'
  source_count: number
  signal_count: number
  sources: string[]
  net_sentiment: number
  avg_score: number
  bullish_pct: number
  bearish_pct: number
  signal_types: string[]
  summary: string
}


// ── Helpers ──────────────────────────────────────────────────────────────────

function gbp(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function gbpFull(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

function pct(n: number): string {
  return `${n >= 0 ? '+' : ''}${(n ?? 0).toFixed(1)}%`
}

function pctFull(n: number): string {
  return `${n >= 0 ? '+' : ''}${(n ?? 0).toFixed(2)}%`
}

function relTime(ts?: string): string {
  if (!ts) return '-'
  try {
    const d = new Date(ts)
    if (isNaN(d.getTime())) return ts
    const diff = Date.now() - d.getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  } catch {
    return ts
  }
}

function convictionBar(conviction: number | string | undefined): string {
  const val = typeof conviction === 'string' ? parseFloat(conviction) : (conviction ?? 0)
  if (val >= 0.8) return 'bg-emerald-400'
  if (val >= 0.6) return 'bg-blue-400'
  if (val >= 0.4) return 'bg-amber-400'
  return 'bg-slate-400'
}

function convictionLabel(conviction: number | string | undefined): string {
  const val = typeof conviction === 'string' ? parseFloat(conviction) : (conviction ?? 0)
  return `${(val * 100).toFixed(0)}%`
}

function statusBadge(status?: string) {
  const s = (status || '').toLowerCase()
  if (s === 'open' || s === 'approved' || s === 'executed')
    return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
  if (s === 'closed')
    return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
  if (s === 'stopped')
    return 'bg-rose-500/15 text-rose-400 border-rose-500/30'
  if (s === 'pending')
    return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
  if (s === 'rejected' || s === 'failed')
    return 'bg-rose-500/15 text-rose-400 border-rose-500/30'
  return 'bg-surface-hover text-text-muted border-border'
}

const DIRECTION_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  bullish: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  bearish: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30' },
  mixed:   { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
}

// Real metrics come from /api/alpha-engine/status. No local synthesis.

function computeAttribution(historyTrades: Trade[]): { source: string; pnl: number }[] {
  const map: Record<string, number> = {}
  for (const t of historyTrades) {
    const sources = t.signal_sources ?? ['Unknown']
    const split = (t.pnl ?? 0) / sources.length
    for (const s of sources) {
      map[s] = (map[s] ?? 0) + split
    }
  }
  return Object.entries(map)
    .map(([source, pnl]) => ({ source, pnl: Math.round(pnl) }))
    .sort((a, b) => b.pnl - a.pnl)
}

// ── KPI Card ────────────────────────────────────────────────────────────────

// Small inline help icon — a one-line hover explains jargon (Sharpe, NAV, etc.)
// without cluttering the label.
function InfoTip({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center cursor-help" title={text}>
      <HelpCircle size={11} className="text-text-muted opacity-50 hover:opacity-100 transition" />
    </span>
  )
}

function KpiCard({ label, value, sub, positive, icon: Icon, info, hero }: {
  label: string
  value: string
  sub?: string
  positive?: boolean
  icon?: LucideIcon
  info?: string
  hero?: boolean
}) {
  return (
    <div className="stat-card hover-lift">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-text-muted" />}
        <p className="stat-label">{label}</p>
        {info && <InfoTip text={info} />}
      </div>
      <p className={clsx(
        hero ? 'stat-hero' : 'stat-value text-xl',
        positive === true && 'text-emerald-400',
        positive === false && 'text-rose-400',
        hero && positive == null && 'text-text-primary',
      )}>
        {value}
      </p>
      {sub && (
        <p className={clsx(
          'text-xs mt-0.5',
          positive === true ? 'text-emerald-400' : positive === false ? 'text-rose-400' : 'text-text-muted',
        )}>
          {sub}
        </p>
      )}
    </div>
  )
}

// Engine Status panel — shows what the autonomous engine is currently doing,
// so traders don't have to cross-reference four other pages to orient.
function EngineStatusPanel({
  regime, regimeMultiplier, openPositions, maxPositions,
  gradeCounts, lastScanAt, nextScanAt, potSize,
}: {
  regime: string | null
  regimeMultiplier: number | null
  openPositions: number
  maxPositions: number
  gradeCounts: Record<string, number>
  lastScanAt: Date | null
  nextScanAt: Date | null
  potSize: number
}) {
  const REGIME_COLORS: Record<string, string> = {
    RISK_ON:       'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    RISK_OFF:      'bg-rose-500/15 text-rose-300 border-rose-500/40',
    VOLATILITY:    'bg-amber-500/15 text-amber-300 border-amber-500/40',
    ROTATION:      'bg-blue-500/15 text-blue-300 border-blue-500/40',
    CONSOLIDATION: 'bg-surface-hover text-text-secondary border-border',
  }
  const regimeCls = REGIME_COLORS[regime || ''] || 'bg-surface-hover text-text-secondary border-border'
  const graded = (gradeCounts.A || 0) + (gradeCounts.B || 0) + (gradeCounts.C || 0) + (gradeCounts.D || 0)

  return (
    <div className="card p-4 border-blue-500/20 bg-gradient-to-br from-blue-500/[0.03] to-transparent">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge size={15} className="text-blue-400" />
          <h2 className="panel-title !text-text-primary">Engine Status</h2>
          <InfoTip text="Live state of the autonomous scanner. Scans run every 2 hours; each scan reads signals, filters by conviction ≥50, and queues trades for approval." />
        </div>
        <span className="text-[10px] text-text-muted">auto-refreshed every minute</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger-children">
        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <Layers size={10} /> Regime
          </div>
          <span className={clsx('inline-block text-[11px] px-2 py-0.5 rounded border font-semibold', regimeCls)}>
            {regime || 'detecting...'}
          </span>
          {regimeMultiplier !== null && (
            <div className="text-[10px] text-text-muted mt-1">
              sizing ×{regimeMultiplier.toFixed(1)}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <Target size={10} /> Pot Size
          </div>
          <div className="text-sm font-mono text-text-primary">£{potSize.toLocaleString('en-GB')}</div>
          <div className="text-[10px] text-text-muted">per Alpha Engine scan</div>
        </div>

        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <Activity size={10} /> Positions
          </div>
          <div className="text-sm font-mono text-text-primary">
            {openPositions} <span className="text-text-muted">/ {maxPositions}</span>
          </div>
          <div className="text-[10px] text-text-muted">{maxPositions - openPositions} slot{maxPositions - openPositions !== 1 ? 's' : ''} free</div>
        </div>

        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <ShieldCheck size={10} /> Graded Sources
          </div>
          <div className="text-sm font-mono text-text-primary">{graded}</div>
          <div className="text-[10px] flex gap-1.5 mt-0.5 font-mono">
            <span className="text-emerald-300">A{gradeCounts.A || 0}</span>
            <span className="text-blue-300">B{gradeCounts.B || 0}</span>
            <span className="text-amber-300">C{gradeCounts.C || 0}</span>
            <span className="text-rose-300">D{gradeCounts.D || 0}</span>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <Clock size={10} /> Last Scan
          </div>
          <div className="text-sm font-mono text-text-primary">
            {lastScanAt ? relTime(lastScanAt.toISOString()) : '—'}
          </div>
          <div className="text-[10px] text-text-muted">reported by the engine</div>
        </div>

        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase text-text-muted mb-1">
            <Clock size={10} /> Next Scan
          </div>
          <div className="text-sm font-mono text-text-primary">
            {/* The status payload carries no next-scan time — show the fixed
                schedule, never a fabricated countdown. */}
            {nextScanAt ? relTime(nextScanAt.toISOString()).replace(' ago', '') : '—'}
          </div>
          <div className="text-[10px] text-text-muted">scheduled 08–22 UTC, every 2h</div>
        </div>
      </div>
    </div>
  )
}

// ── Custom Tooltip ──────────────────────────────────────────────────────────

function NavTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-surface-card border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="text-text-secondary mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {gbp(p.value)}
        </p>
      ))}
    </div>
  )
}

function BarTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null
  const d = payload[0]
  return (
    <div className="bg-surface-card border border-border rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="text-text-secondary mb-1">{d.payload.source}</p>
      <p className={d.value >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
        P&L: {gbpFull(d.value)}
      </p>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────────────────────

// "How it works" explainer — one block, collapsible so it doesn't dominate.
// Answers the three most common questions: WHAT it reads, WHEN it runs,
// and HOW it decides. Keeps the page transparent without forcing jargon.
function HowItWorks() {
  const [open, setOpen] = useState(false)
  return (
    <div className="card p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-hover/40 transition"
      >
        <div className="flex items-center gap-2">
          <Info size={15} className="text-blue-400" />
          <span className="text-sm font-semibold text-text-primary">How the Alpha Engine decides</span>
          <span className="text-[10px] text-text-muted">— click for detail</span>
        </div>
        <span className={clsx('text-text-muted text-xs transition', open && 'rotate-90')}>▸</span>
      </button>
      {open && (
        <div className="border-t border-border px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-5 text-xs">
          <div>
            <div className="panel-title mb-1.5">
              1 · Inputs
            </div>
            <p className="text-text-secondary leading-relaxed">
              Reads <span className="text-text-primary">51 signal feeds</span> from the DB (regulatory: SEC/FCA filings,
              insider trades, congressional trades · political: lobbying, gov contracts · alternative: analyst
              upgrades, options flow, patents, on-chain, ETF flows · social: news, Substack, Reddit, StockTwits,
              PhD agent swarm).
            </p>
            <p className="text-text-secondary mt-2">
              Each source carries a <span className="text-emerald-300 font-mono">backtest grade</span> (A/B/C/D) updated
              daily — proven sources get a conviction bonus, noisy ones get a penalty.
            </p>
          </div>

          <div>
            <div className="panel-title mb-1.5">
              2 · Pattern detection & scoring
            </div>
            <p className="text-text-secondary leading-relaxed">
              Four patterns are hunted each scan:
              <span className="text-text-primary"> multi-source convergence</span> (2+ independent sources agreeing),
              <span className="text-text-primary"> insider clusters</span> (2+ insiders buying within 7d),
              <span className="text-text-primary"> momentum shifts</span>, and
              <span className="text-text-primary"> agent consensus</span> (PhD swarm agreement).
            </p>
            <p className="text-text-secondary mt-2">
              Each opportunity scores 0–100 = base (pattern type) + source diversity + regulatory 3× weighting +
              recency decay + grade bonus. Below 50 conviction is discarded.
            </p>
          </div>

          <div>
            <div className="panel-title mb-1.5">
              3 · Sizing & risk
            </div>
            <p className="text-text-secondary leading-relaxed">
              Half-Kelly sizing against a £20k pot: 90+ conviction = full size (max £4k), 70–89 = half,
              50–69 = quarter. The market regime applies a multiplier (<span className="font-mono">RISK_ON ×1.2, RISK_OFF ×0.5,
              VOLATILITY ×0.6</span>).
            </p>
            <p className="text-text-secondary mt-2">
              Hard caps: max 5 open positions, 40% per sector, 60% per region, −10% stop-loss per position,
              −15% circuit breaker pauses all new trades.
            </p>
          </div>

          <div className="md:col-span-3 pt-3 border-t border-border/50">
            <div className="panel-title mb-1">
              Cadence
            </div>
            <p className="text-text-secondary">
              Signals refresh every 15 min during market hours (30 min off-peak) · Alpha Engine scans at
              <span className="font-mono text-text-primary"> 08, 10, 12, 14, 16, 18, 20, 22 UTC</span> · backtest grades refresh
              daily at 06:00 UTC (before the first scan).
            </p>
            <p className="text-text-secondary mt-2">
              For the full picture of where each component runs and what table is authoritative, see{' '}
              <a href="/app/system-map" className="text-blue-400 hover:underline">/app/system-map</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

interface EngineStatus {
  last_scan_at: string | null
  last_scan_trades: number
  open_positions: number
  max_positions: number
  pot_size_gbp: number
  current_nav: number
  peak_nav: number
  sharpe_ratio: number | null
  max_drawdown_pct: number | null
  win_rate: number | null
  total_return_pct: number | null
  has_ever_run: boolean
  running: boolean
  paused: boolean
}

interface NavPointReal {
  date: string
  nav: number
  spy?: number
}

export default function AlphaEnginePage() {
  const [activeTrades, setActiveTrades] = useState<Trade[]>([])
  const [historyTrades, setHistoryTrades] = useState<Trade[]>([])
  const [convergences, setConvergences] = useState<Convergence[]>([])
  const [sourceGrades, setSourceGrades] = useState<Record<string, string>>({})
  const [gradeCounts, setGradeCounts] = useState<Record<string, number>>({})
  const [regime, setRegime] = useState<string | null>(null)
  const [regimeMultiplier, setRegimeMultiplier] = useState<number | null>(null)
  const [status, setStatus] = useState<EngineStatus | null>(null)
  const [navSeries, setNavSeries] = useState<NavPointReal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Backend soft-errors with {error: "engine_unavailable", detail} instead of
  // 500ing — that must NOT read as "Never run".
  const [engineUnavailable, setEngineUnavailable] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const engineRunning = status?.running ?? false
  const hasEverRun = status?.has_ever_run ?? false
  const paused = status?.paused ?? false
  const latestNav = status?.current_nav ?? 0
  const peakNav = status?.peak_nav ?? 0
  const navChange = peakNav > 0 ? ((latestNav - peakNav) / peakNav) * 100 : 0
  const sharpe = status?.sharpe_ratio ?? null
  const winRate = status?.win_rate ?? null
  const totalReturn = status?.total_return_pct ?? null
  const maxDrawdown = status?.max_drawdown_pct ?? null

  const attribution = computeAttribution(historyTrades)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [positionsRes, convRes, sourcesRes, regimeRes, statusRes, perfRes] = await Promise.allSettled([
        api.get('/api/alpha-engine/positions?limit=100'),
        api.get('/api/signals/convergences'),
        api.get('/api/signals/sources'),
        api.get('/api/market-regime'),
        api.get('/api/alpha-engine/status'),
        api.get('/api/alpha-engine/performance?days=90'),
      ])

      if (statusRes.status === 'fulfilled') {
        const sData = statusRes.value.data
        if (sData?.error === 'engine_unavailable') {
          setEngineUnavailable(String(sData?.detail ?? 'Engine status endpoint is unavailable'))
          setStatus(null)
        } else {
          setEngineUnavailable(null)
          setStatus(sData as EngineStatus)
        }
      }
      if (perfRes.status === 'fulfilled') {
        setNavSeries((perfRes.value.data?.nav_series ?? []) as NavPointReal[])
      }

      if (positionsRes.status === 'fulfilled') {
        const allPositions = (positionsRes.value.data?.positions ?? []) as Trade[]
        setActiveTrades(allPositions.filter((p: any) => p.status === 'open'))
        setHistoryTrades(allPositions.filter((p: any) => p.status === 'closed' || p.status === 'stopped'))
      }
      if (convRes.status === 'fulfilled') {
        setConvergences(convRes.value.data?.convergences ?? [])
      }
      if (sourcesRes.status === 'fulfilled') {
        const grades: Record<string, string> = {}
        const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 }
        for (const s of (sourcesRes.value.data?.sources ?? []) as Array<{ name: string; backtest_grade?: string | null }>) {
          if (s.backtest_grade) {
            grades[s.name] = s.backtest_grade
            counts[s.backtest_grade] = (counts[s.backtest_grade] ?? 0) + 1
          }
        }
        setSourceGrades(grades)
        setGradeCounts(counts)
      }
      if (regimeRes.status === 'fulfilled') {
        const r = regimeRes.value.data?.regime ?? null
        setRegime(r)
        const REGIME_SIZING: Record<string, number> = {
          RISK_ON: 1.2, RISK_OFF: 0.5, VOLATILITY: 0.6, ROTATION: 0.8, CONSOLIDATION: 1.0,
        }
        setRegimeMultiplier(r ? (REGIME_SIZING[r] ?? 1.0) : null)
      }

      const anyFailed = [positionsRes, convRes, statusRes].some(r => r.status === 'rejected')
      if (anyFailed) {
        setError('Some data sources unavailable. Showing partial data.')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch data')
    } finally {
      setLoading(false)
      setLastRefresh(new Date())
    }
  }, [])

  useEffect(() => {
    fetchData()
    const iv = setInterval(fetchData, 60_000)
    return () => clearInterval(iv)
  }, [fetchData])

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Zap size={24} className="text-amber-400" />
            Alpha Engine
          </h1>
          <p className="text-sm text-text-muted mt-0.5">
            Autonomous scanner. Reads 51 signal feeds · runs every 2 hours · queues trades at 50+ conviction.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className={clsx(
            !engineUnavailable && !paused && engineRunning
              ? 'chip-live'
              : clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border',
                  engineUnavailable
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    : paused
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    : hasEverRun
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    : 'bg-surface-hover text-text-muted border-border',
                ),
          )}
            title={
              engineUnavailable
                ? `Engine status endpoint unavailable: ${engineUnavailable}`
                : paused
                ? 'Engine is paused — scans will short-circuit until resumed'
                : engineRunning
                ? 'A scan completed within the last 4 hours'
                : hasEverRun
                ? 'Engine has run but not in the last 4 hours — scheduler may be down'
                : 'Engine has never run on this deployment'
            }
          >
            {engineUnavailable ? <AlertTriangle size={12} /> : paused ? <Pause size={12} /> : engineRunning ? <Play size={12} /> : <Pause size={12} />}
            {engineUnavailable ? 'Status unavailable' : paused ? 'Paused' : engineRunning ? 'Running' : hasEverRun ? 'Idle' : 'Never run'}
          </span>

          <span className="text-xs text-text-muted flex items-center gap-1">
            <Clock size={12} />
            Last scan: {engineUnavailable ? '—' : status?.last_scan_at ? relTime(status.last_scan_at) : 'never'}
          </span>

          <button
            type="button"
            onClick={async () => {
              try {
                await api.post(paused ? '/api/alpha-engine/resume' : '/api/alpha-engine/pause')
                fetchData()
              } catch (e) {
                console.error('pause/resume failed', e)
              }
            }}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition',
              paused
                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20'
                : 'bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20',
            )}
            title={paused ? 'Resume scans' : 'Pause scans (NAV snapshots continue)'}
          >
            {paused ? <Play size={12} /> : <Pause size={12} />}
            {paused ? 'Resume' : 'Pause'}
          </button>

          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="btn-ghost p-2"
            title="Refresh"
          >
            <RefreshCw size={16} className={clsx(loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* ── Error Banner ────────────────────────────────────────────────────── */}
      {error && (
        <div className="card p-3 border-amber-500/30 bg-amber-500/5 flex items-center gap-2 text-sm text-amber-400">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* ── Engine-unavailable Banner ───────────────────────────────────────── */}
      {engineUnavailable && (
        <div className="card p-3 border-amber-500/30 bg-amber-500/5 flex items-center gap-2 text-sm text-amber-400">
          <AlertTriangle size={16} />
          Engine status is currently unavailable — the metrics below may be stale or missing.
          <span className="text-text-muted text-xs">({engineUnavailable})</span>
        </div>
      )}

      {/* ── Engine Status Panel ─────────────────────────────────────────────── */}
      <EngineStatusPanel
        regime={regime}
        regimeMultiplier={regimeMultiplier}
        openPositions={status?.open_positions ?? activeTrades.length}
        maxPositions={status?.max_positions ?? 5}
        gradeCounts={gradeCounts}
        lastScanAt={status?.last_scan_at ? new Date(status.last_scan_at) : null}
        nextScanAt={null}
        potSize={status?.pot_size_gbp ?? 20000}
      />

      {/* ── How It Works ────────────────────────────────────────────────────── */}
      <HowItWorks />

      {/* ── KPI Row — real metrics, honest "—" when no data ───────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <KpiCard
          icon={BarChart2}
          hero
          label="Auto-Pot NAV"
          value={latestNav > 0 ? gbp(latestNav) : '—'}
          sub={latestNav > 0
            ? (peakNav > latestNav ? `${navChange.toFixed(1)}% below peak` : 'at peak')
            : 'no NAV snapshots yet'}
          positive={latestNav > 0 ? (navChange >= 0) : undefined}
          info="Current value of the autonomous trading pot, taken from the alpha_engine_nav snapshots table. Populates once the engine records its first daily NAV."
        />
        <KpiCard
          icon={Target}
          label="Win Rate"
          value={winRate !== null ? `${winRate.toFixed(0)}%` : '—'}
          sub={winRate !== null ? 'over closed trades' : 'no closed trades yet'}
          positive={winRate !== null ? (winRate >= 55 ? true : winRate < 45 ? false : undefined) : undefined}
          info="Fraction of closed trades that finished profitable. 55%+ is healthy for a multi-signal strategy; 45%− suggests signal decay."
        />
        <KpiCard
          icon={Activity}
          label="Sharpe Ratio"
          value={sharpe !== null ? sharpe.toFixed(2) : '—'}
          sub={sharpe !== null ? 'risk-adjusted return' : 'needs 2+ NAV days'}
          positive={sharpe !== null ? (sharpe >= 1 ? true : sharpe < 0 ? false : undefined) : undefined}
          info="Annualised mean daily return divided by the standard deviation of daily returns, net of 5% risk-free rate. Above 1.0 is good, above 2.0 is rare."
        />
        <KpiCard
          icon={Trophy}
          label="Total Return"
          value={totalReturn !== null ? pct(totalReturn) : '—'}
          sub={maxDrawdown !== null ? `max DD ${maxDrawdown.toFixed(1)}%` : 'no data yet'}
          positive={totalReturn !== null ? (totalReturn >= 0) : undefined}
          info="Cumulative percentage return of the auto-pot NAV over the tracking window. Max drawdown shows the worst peak-to-trough decline."
        />
      </div>

      {/* ── NAV Chart — only when real data exists ─────────────────────────── */}
      {navSeries.length >= 2 ? (
        <div className="card p-5">
          {(() => {
            const first = navSeries[0]?.nav ?? 0
            const last = navSeries[navSeries.length - 1]?.nav ?? 0
            const navReturn = first > 0 ? ((last - first) / first) * 100 : 0
            const spyFirst = navSeries[0]?.spy
            const spyLast = navSeries[navSeries.length - 1]?.spy
            const spyReturn = spyFirst && spyFirst > 0 && spyLast ? ((spyLast - spyFirst) / spyFirst) * 100 : null
            const alpha = spyReturn !== null ? navReturn - spyReturn : null
            return (
              <>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="panel-title !text-text-primary flex items-center gap-2">
                    Auto-Pot NAV vs S&amp;P 500
                    <InfoTip text="Daily NAV snapshots rebased to the starting pot, with SPY rebased to the same starting £ amount. Alpha = engine return − SPY return over the same window." />
                  </h2>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-text-muted">NAV</span>
                      <span className={clsx('font-mono', navReturn >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                        {navReturn >= 0 ? '+' : ''}{navReturn.toFixed(2)}%
                      </span>
                    </span>
                    {spyReturn !== null && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-400" />
                        <span className="text-text-muted">SPY</span>
                        <span className={clsx('font-mono', spyReturn >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                          {spyReturn >= 0 ? '+' : ''}{spyReturn.toFixed(2)}%
                        </span>
                      </span>
                    )}
                    {alpha !== null && (
                      <span className="flex items-center gap-1">
                        <span className="text-text-muted">α</span>
                        <span className={clsx('font-mono font-semibold', alpha >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                          {alpha >= 0 ? '+' : ''}{alpha.toFixed(2)}%
                        </span>
                      </span>
                    )}
                    <span className="text-text-muted">· {navSeries.length} days</span>
                  </div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={navSeries} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <defs>
                        <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#34d399" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="spyGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: 'rgb(var(--text-muted))', fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                        tickFormatter={(v: string) => String(v).slice(5, 10)}
                      />
                      <YAxis
                        tick={{ fill: 'rgb(var(--text-muted))', fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}k`}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip content={<NavTooltip />} />
                      <Area type="monotone" dataKey="nav" name="NAV" stroke="#34d399" strokeWidth={2} fill="url(#navGrad)" />
                      {spyReturn !== null && (
                        <Area
                          type="monotone"
                          dataKey="spy"
                          name="S&P 500 (rebased)"
                          stroke="#60a5fa"
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          fill="url(#spyGrad)"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            )
          })()}
        </div>
      ) : (
        <div className="card p-6 border-dashed">
          <div className="flex items-start gap-3">
            <BarChart2 size={20} className="text-text-muted opacity-50 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-1">NAV chart not available yet</h3>
              <p className="text-xs text-text-muted max-w-2xl">
                The Alpha Engine records a daily NAV snapshot into the
                {' '}<code className="text-text-secondary">alpha_engine_nav</code>{' '}
                table once per day at 21:00 UTC (after US close). The chart and
                its derived metrics (Sharpe, max drawdown, total return) appear
                after at least 2 daily snapshots exist —{' '}
                {status
                  ? (hasEverRun
                    ? 'the engine has scanned but no NAV has been recorded, so the scheduler task for NAV snapshots may not be running yet.'
                    : 'the engine has never run on this deployment — once the 2-hour scheduler fires its first scan and a day passes, the snapshot lands here.')
                  : 'check back once /api/alpha-engine/status responds.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Active Trades ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="panel-title !text-text-primary flex items-center gap-2">
            <Activity size={16} className="text-blue-400" />
            Active Trades
          </h2>
          <span className="text-xs text-text-muted">
            {activeTrades.length} position{activeTrades.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading && activeTrades.length === 0 ? (
          <div className="p-4 space-y-2">
            <div className="skeleton h-8" />
            <div className="skeleton h-8" />
            <div className="skeleton h-8" />
          </div>
        ) : activeTrades.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-text-muted text-sm mb-2">No trades in the queue right now</div>
            <div className="text-xs text-text-muted max-w-md mx-auto">
              The engine only queues a trade when it finds an opportunity with 50+ conviction.
              Next scan runs on the 2-hour cadence (08/10/12/14/16/18/20/22 UTC) or hit Refresh to pull
              fresh signals now. High-conviction convergences appear below.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Action</th>
                  <th className="!text-right">Qty</th>
                  <th className="!text-right">Entry</th>
                  <th className="!text-right">Current</th>
                  <th className="!text-right">P&L</th>
                  <th className="!text-center">
                    <span className="inline-flex items-center gap-1">
                      Conviction
                      <InfoTip text="0–100 score. 90+ full size · 70–89 half · 50–69 quarter. Below 50 never queues." />
                    </span>
                  </th>
                  <th className="!text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {activeTrades.map((t, i) => {
                  const pnl = t.pnl ?? ((t.current_price ?? 0) - (t.entry_price ?? 0)) * (t.qty ?? 0)
                  const isUp = pnl >= 0
                  return (
                    <tr
                      key={t.id ?? `${t.ticker}-${i}`}
                    >
                      <td>
                        <a
                          href={`/app/stock/${encodeURIComponent(t.ticker)}`}
                          className="font-medium text-blue-400 hover:underline"
                        >
                          {t.ticker}
                        </a>
                        {(t as any).pattern && (
                          <div className="text-[10px] text-text-muted mt-0.5 capitalize">
                            {String((t as any).pattern).replace(/_/g, ' ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={clsx(
                          t.action === 'BUY' ? 'delta-chip-pos' : 'delta-chip-neg',
                        )}>
                          {t.action === 'BUY' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {t.action}
                        </span>
                      </td>
                      <td className="text-right text-text-secondary">{t.qty ?? '-'}</td>
                      <td className="text-right text-text-secondary">
                        {t.entry_price ? gbpFull(t.entry_price) : '-'}
                      </td>
                      <td className="text-right text-text-secondary">
                        {t.current_price ? gbpFull(t.current_price) : '-'}
                      </td>
                      <td className={clsx(
                        'text-right font-medium',
                        isUp ? 'text-emerald-400' : 'text-rose-400',
                      )}>
                        {pnl !== 0 ? gbpFull(pnl) : '-'}
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-12 h-1.5 rounded-full bg-surface-hover overflow-hidden">
                            <div
                              className={clsx('h-full rounded-full', convictionBar(t.conviction))}
                              style={{ width: convictionLabel(t.conviction) }}
                            />
                          </div>
                          <span className="text-xs text-text-muted">{convictionLabel(t.conviction)}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className={clsx(
                          'inline-block px-2 py-0.5 text-xs rounded-full border',
                          statusBadge(t.status),
                        )}>
                          {t.status ?? 'pending'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Trade History ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="panel-title !text-text-primary flex items-center gap-2">
            <Clock size={16} className="text-text-muted" />
            Trade History
          </h2>
          <span className="text-xs text-text-muted">
            {historyTrades.length} completed trade{historyTrades.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading && historyTrades.length === 0 ? (
          <div className="p-4 space-y-2">
            <div className="skeleton h-8" />
            <div className="skeleton h-8" />
            <div className="skeleton h-8" />
          </div>
        ) : historyTrades.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-text-muted text-sm mb-2">No completed trades yet</div>
            <div className="text-xs text-text-muted max-w-md mx-auto">
              Queued trades close either on the −10% stop-loss or when the thesis is realised.
              Once trades close, attribution by signal source becomes meaningful — until then the
              attribution chart below is empty.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Action</th>
                  <th className="!text-right">Entry</th>
                  <th className="!text-right">Exit</th>
                  <th className="!text-right">P&L %</th>
                  <th className="!text-center">Conviction</th>
                  <th>Signal Sources</th>
                  <th className="!text-right">Duration</th>
                </tr>
              </thead>
              <tbody>
                {historyTrades.slice(0, 25).map((t, i) => {
                  const pnlPct = t.pnl_pct ?? 0
                  const isUp = pnlPct >= 0
                  return (
                    <tr
                      key={t.id ?? `hist-${t.ticker}-${i}`}
                    >
                      <td>
                        <a
                          href={`/app/stock/${encodeURIComponent(t.ticker)}`}
                          className="font-medium text-blue-400 hover:underline"
                        >
                          {t.ticker}
                        </a>
                        {(t as any).pattern && (
                          <div className="text-[10px] text-text-muted mt-0.5 capitalize">
                            {String((t as any).pattern).replace(/_/g, ' ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={clsx(
                          t.action === 'BUY' ? 'delta-chip-pos' : 'delta-chip-neg',
                        )}>
                          {t.action === 'BUY' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {t.action}
                        </span>
                      </td>
                      <td className="text-right text-text-secondary">
                        {t.entry_price ? gbpFull(t.entry_price) : '-'}
                      </td>
                      <td className="text-right text-text-secondary">
                        {t.exit_price ? gbpFull(t.exit_price) : '-'}
                      </td>
                      <td className={clsx(
                        'text-right font-medium',
                        isUp ? 'text-emerald-400' : 'text-rose-400',
                      )}>
                        {pctFull(pnlPct)}
                        {t.status === 'stopped' && (
                          <span className="ml-1 text-[9px] px-1 py-0 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30" title="Closed by −10% stop-loss">
                            SL
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-12 h-1.5 rounded-full bg-surface-hover overflow-hidden">
                            <div
                              className={clsx('h-full rounded-full', convictionBar(t.conviction))}
                              style={{ width: convictionLabel(t.conviction) }}
                            />
                          </div>
                          <span className="text-xs text-text-muted">{convictionLabel(t.conviction)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {(t.signal_sources ?? []).map(s => {
                            const grade = sourceGrades[s]
                            const cls = grade === 'A'
                              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                              : grade === 'B'
                              ? 'bg-blue-500/15 text-blue-300 border-blue-500/40'
                              : grade === 'C'
                              ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                              : grade === 'D'
                              ? 'bg-rose-500/15 text-rose-300 border-rose-500/40'
                              : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            return (
                              <span
                                key={s}
                                title={grade ? `Backtest grade: ${grade}` : undefined}
                                className={clsx('inline-block px-1.5 py-0.5 text-[10px] rounded border', cls)}
                              >
                                {s}{grade ? ` ${grade}` : ''}
                              </span>
                            )
                          })}
                          {(!t.signal_sources || t.signal_sources.length === 0) && (
                            <span className="text-text-muted text-xs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="text-right text-text-secondary text-xs">
                        {t.duration ?? '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Bottom Two-Column: Attribution + Convergences ───────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Signal Attribution ─────────────────────────────────────────────── */}
        <div className="card p-5">
          <h2 className="panel-title !text-text-primary mb-4 flex items-center gap-2">
            <BarChart2 size={16} className="text-purple-400" />
            Signal Attribution
            <span className="text-[10px] font-normal text-text-muted">· P&amp;L per source</span>
            <InfoTip text="Each closed trade's P&L is split evenly across the sources that generated it. Shows which feeds are actually contributing alpha vs riding along." />
          </h2>

          {attribution.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-center text-text-muted text-xs px-4">
              Attribution appears after trades close. Each trade's P&amp;L will be split
              across the sources that fired its signal, revealing which feeds drive alpha.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={attribution}
                  layout="vertical"
                  margin={{ top: 0, right: 20, bottom: 0, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: 'rgb(var(--text-muted))', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => gbp(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="source"
                    tick={{ fill: 'rgb(var(--text-secondary))', fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={100}
                  />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]} maxBarSize={24}>
                    {attribution.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={entry.pnl >= 0 ? '#34d399' : '#fb7185'}
                        fillOpacity={0.8}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* ── Latest Convergences ────────────────────────────────────────────── */}
        <div className="card p-5">
          <h2 className="panel-title !text-text-primary mb-4 flex items-center gap-2">
            <Zap size={16} className="text-amber-400" />
            Latest Convergences
            <InfoTip text="Tickers where 2+ independent sources (e.g. SEC filing + insider buy + analyst upgrade) pointed the same direction in the last 24h — the highest-conviction pattern the engine hunts." />
          </h2>

          {loading && convergences.length === 0 ? (
            <div className="space-y-3">
              <div className="skeleton h-14" />
              <div className="skeleton h-14" />
              <div className="skeleton h-14" />
            </div>
          ) : convergences.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-center text-text-muted text-xs px-4">
              No tickers with 2+ agreeing sources in the last 24h. The engine will scan again on the
              next 2-hour cadence — quiet market periods produce fewer convergences.
            </div>
          ) : (
            <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
              {convergences.slice(0, 8).map((c, i) => {
                const ds = DIRECTION_STYLES[c.direction] ?? DIRECTION_STYLES.mixed
                return (
                  <div
                    key={`${c.ticker}-${i}`}
                    className={clsx(
                      'rounded-lg border p-3',
                      ds.bg, ds.border,
                    )}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-text-primary text-sm">{c.ticker}</span>
                        <span className={clsx(
                          'text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded',
                          ds.text, ds.bg,
                        )}>
                          {c.direction}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-text-muted">
                        <span>{c.source_count} sources</span>
                        <span className="text-text-muted/40">|</span>
                        <span>{c.signal_count} signals</span>
                      </div>
                    </div>

                    <p className="text-xs text-text-secondary line-clamp-2 mb-2">
                      {c.summary}
                    </p>

                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full bg-surface-hover overflow-hidden flex">
                        <div
                          className="h-full bg-emerald-400 rounded-l-full"
                          style={{ width: `${c.bullish_pct}%` }}
                        />
                        <div
                          className="h-full bg-rose-400 rounded-r-full"
                          style={{ width: `${c.bearish_pct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-text-muted whitespace-nowrap">
                        Score: {(c.avg_score ?? 0).toFixed(1)}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {(c.sources ?? []).map(s => (
                        <span
                          key={s}
                          className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-surface-hover text-text-muted border border-border"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
